import {
  calculateEffortRate,
  haveValue,
  getCustomIncomeDetails,
  convertCurrencyFormat,
  getUserDisplayName,
  resolveIncomeSelection,
} from "../helper/common";

export const calculateParticipantRentAmount = (
  participantInfo,
  profile,
  user,
  _t,
  deaWorkspace
) => {
  try {
    if (!participantInfo) {
      return {
        combinedIncome: 0,
        effort_rate: 0,
        profile_effort_rate_percentage: 0,
      };
    }

    const user_income_sources = resolveIncomeSelection(
      profile?.income_selection,
      participantInfo?._id
    );
    const fallbackIncome = profile?.useFallbackIncome;
    const useFallbackIncome =
      typeof fallbackIncome === "boolean" ? fallbackIncome : true;
    // const gurantor_income_sources = user_income_sources?.guarantor_sources;
    const gurantor_income_sources =
      user_income_sources?.[participantInfo?._id]?.guarantor_sources ??
      user_income_sources?.guarantor_sources;
    const disabledIncomeSourceIds = profile?.excluded_income_source_ids ?? [];
    let profile_effort_rate_percentage =
      profile?.effort_rate_data?.tenant?.effort_rate_percentage;
    if (!haveValue(profile_effort_rate_percentage)) {
      profile_effort_rate_percentage =
        user?.current_workspace?.default_effort_rate ?? 35;
    }
    const effortRateType =
      profile?.effort_rate_data?.tenant?.effort_rate_type ??
      profile?.tenantid?.effort_rate_type ??
      "tenant";

    const tInfo = participantInfo;
    const user_income = getCustomIncomeDetails(
      tInfo,
      user,
      _t,
      user_income_sources?.tenant_source,
      undefined,
      //   undefined,
      true,
      deaWorkspace,
      profile?.custom_income_sources
    );

    let totalGurantorIncome = 0;
    let allGurantorIncomes = [];
    let allGurantors = tInfo?.gurantor_id;

    if (Array.isArray(allGurantors)) {
      allGurantors.forEach((gurantor) => {
        if (haveValue(gurantor)) {
          const income = getCustomIncomeDetails(
            gurantor,
            user,
            _t,
            gurantor_income_sources?.[gurantor?._id],
            undefined,
            // useFallbackIncome,
            true,
            deaWorkspace
          );
          if (["guarantor", "both"]?.includes(effortRateType)) {
            totalGurantorIncome += income?.amount || 0;
          }
          allGurantorIncomes.push(income);
        }
      });
    }

    const userAmount = ["tenant", "both"]?.includes(effortRateType)
      ? +user_income?.amount
      : 0;

    const combinedIncome = userAmount + +totalGurantorIncome;

    const data = {
      ...profile,
      tenantid: tInfo,
      rent_percentage: profile_effort_rate_percentage,
    };
    let temp_obj = calculateEffortRate(combinedIncome, user, data);

    return {
      combinedIncome,
      effort_rate: !Number.isNaN(temp_obj?.effort_amount)
        ? Math.round(Number(temp_obj.effort_amount))
        : 0,
      profile_effort_rate_percentage,
    };
  } catch (error) {
    console.error("Error in calculateParticipantRentAmount:", error);
    return {
      combinedIncome: 0,
      effort_rate: 0,
      profile_effort_rate_percentage: 0,
    };
  }
};

export const aggregateApplicationData = (
  profile,
  user,
  _t,
  deaWorkspace,
  checkSalary
) => {
  try {
    if (!profile?.application_id) {
      return {
        tenantName: profile?.tenantid ? "Unknown Application" : "Unknown",
        totalScore: 0,
        totalIncome: 0,
        totalEffortRate: 0,
        totalFlatmates: 0,
        totalGuarantors: 0,
        linkedProperties: profile?.propertyid || [],
        latestUpdateDate: profile?.sortDate,
      };
    }

    const acceptedParticipants =
      profile?.application_id?.participants?.filter(
        (p) => p.consent_status === "accepted" && p.tenant_info
      ) || [];

    if (acceptedParticipants.length === 0) {
      const owner = profile?.application_id?.participants?.find(
        (p) => p.id === profile?.application_id?.created_by
      );
      const ownerName = owner?.tenant_info
        ? getUserDisplayName(owner?.tenant_info)
        : "";
      return {
        tenantName: ownerName
          ? ownerName
          : profile?.application_id?.title || "Unknown Application",
        totalScore: 0,
        totalIncome: 0,
        totalEffortRate: 0,
        totalFlatmates: 0,
        totalGuarantors: 0,
        linkedProperties: profile?.propertyid || [],
        latestUpdateDate: profile?.sortDate,
      };
    }

    let totalScore = 0;
    let totalIncome = 0;
    let totalEffortRate = 0;
    let totalFlatmates = 0;
    let totalGuarantors = 0;
    let latestUpdateDate = null;

    acceptedParticipants.forEach((participant) => {
      const tenantInfo = participant.tenant_info;

      if (checkSalary && typeof checkSalary === "function" && tenantInfo) {
        try {
          const salaryResult = checkSalary(tenantInfo);
          if (salaryResult) {
            totalScore += salaryResult.matched || 0;
          }
        } catch (error) {
          console.error("Error checking salary:", error);
        }
      }

      try {
        const rentAmount = calculateParticipantRentAmount(
          tenantInfo,
          { ...participant, ...profile },
          user,
          _t,
          deaWorkspace
        );
        totalIncome += rentAmount.combinedIncome || 0;
        totalEffortRate += rentAmount.effort_rate || 0;
      } catch (error) {
        console.error("Error calculating rent amount:", error);
      }

      totalFlatmates += tenantInfo?.sharers?.length || 0;
      totalGuarantors += (tenantInfo?.gurantor_id || []).length;

      const participantUpdateDate = tenantInfo?.sortDate;
      if (participantUpdateDate) {
        if (
          !latestUpdateDate ||
          new Date(participantUpdateDate) > new Date(latestUpdateDate)
        ) {
          latestUpdateDate = participantUpdateDate;
        }
      }
    });

    if (!latestUpdateDate) {
      latestUpdateDate = profile?.sortDate;
    }
    const owner = profile?.application_id?.participants?.find(
      (p) => p.id === profile?.application_id?.created_by
    );
    const ownerName = owner?.tenant_info
      ? getUserDisplayName(owner?.tenant_info)
      : "";

    return {
      tenantName: ownerName
        ? ownerName
        : profile?.application_id?.title || "Unknown Application",
      totalScore,
      totalIncome,
      totalEffortRate,
      totalFlatmates,
      totalGuarantors,
      linkedProperties: profile?.propertyid || [],
      latestUpdateDate,
    };
  } catch (error) {
    console.error("Error in aggregateApplicationData:", error);
    return {
      tenantName: "Error Loading Application",
      totalScore: 0,
      totalIncome: 0,
      totalEffortRate: 0,
      totalFlatmates: 0,
      totalGuarantors: 0,
      linkedProperties: [],
      latestUpdateDate: null,
    };
  }
};
